<template>
  <div>
    <breadcrumbs :home="vessel.name + ': Move contents to a bulk container'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
            <v-row dense>
              <v-col cols="6" sm="4" lg="3">
                <date-picker v-model="transaction.date" @change="refresh"/>
              </v-col>
            <v-col class="text-caption ml-5 mt-3 grey--text">{{vesselContents}}</v-col>
            </v-row>
            <v-row dense>
              <v-col cols="6" sm="3" lg="2">
                <alcohol-input label="Volume" :spirit="spirit || !this.vessel.contents.volume" :tare="vessel.tare"
                                v-model="transaction.volume" :litres.sync="transaction.litres" :kg.sync="transaction.kg" :strength.sync="transaction.strength"
                                :disabled="!edit" :hint="vesselVolume"/>
              </v-col>
              <v-col>
                <v-btn x-small text class="mt-3" @click="allContents()">
                  Use all contents
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="transactionId==undefined">
              <v-col>
                <v-radio-group v-model="create" row persistent-hint class="mt-0"
                              :hint="bulkContainers.length===0?'(No valid existing bulk containers found)':''">
                  <v-radio :value="false" label="Use existing bulk container" :disabled="bulkContainers.length===0"/>
                  <v-radio :value="true" label="Create new bulk container"/>
                </v-radio-group>
              </v-col>
            </v-row>

            <v-row v-if="create">
              <v-col cols="6" sm="3" lg="2">
                <v-text-field label="New bulk container #" v-model="transaction.number" :rules="[fieldRules.required,fieldRules.maxLength(10)]" :counter="10" outlined dense/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input label="Shipping weight" v-model="transaction.weight" prefix="" suffix="kg" :precision="3"/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <v-text-field label="Location" v-model="transaction.location" :rules="[fieldRules.maxLength(20)]" :counter="20" outlined dense/>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12" sm="6" lg="4">
                <bulk-container-select v-model="transaction.destinationBulkContainerId" :bulkContainers="bulkContainers" label="Destination" outlined
                                      :highlight="bulk => bulk.contents.alcoholId === vessel.contents.alcoholId" :hint="transactionId===undefined"/>
              </v-col>
            </v-row>

            <v-row dense >
              <v-col cols="12">
                <v-text-field label="Comments" v-model="transaction.comments" outlined dense counter="255" :rules="[fieldRules.maxLength(255)]"/>
              </v-col>
            </v-row>
          </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import BulkContainerSelect from '@/components/BulkContainerSelect'

export default {
  name: 'ExciseVesselTransferToBulkContainer',
  components: {BulkContainerSelect},
  mixins: [confirmLeave],
	props: {
    vesselId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      init: true,
      edit: false,
      create: false,
      vessel: {name:'',contents:{}},
      valid:true,
      saving:false,
      bulkContainers: [],
      transaction: {vesselId:this.vesselId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    volume: 0, litres: 0, kg:0, strength:0, comments:'', destinationBulkContainerId:'', weight:0, number:0,location:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    spirit() {
      return this.vessel.contents?.alcoholType === 'Spirit';
    },
    vesselContents() {
      return this.vessel.contents.volume ? this.vessel.contents.alcoholName : '';
    },
    vesselVolume() {
      if (!this.transactionId)
        return this.vessel.contents.volume ? `${this.$number(this.vessel.contents.volume, 3)} ${this.spirit?'LAA':'litres'}` : '(empty)';
    },
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    const results = await this.$apiCall(`query ExciseVesselTransferToBulkContainer($id:ID!,$edit:Boolean!) {
                                            exciseVesselTransferToBulkContainer(id:$id) @include(if:$edit) {
                                              id,vesselId,date,volume,litres,kg,strength,location,destinationBulkContainerId,comments
                                            }
                                            nextBulkContainerNumber
                                          }`,
                                        {edit:this.transactionId!=undefined,id:this.transactionId||''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    if (this.transactionId) {
      this.transaction = results.data.exciseVesselTransferToBulkContainer;
      this.transaction.number = 1; this.transaction.weight = 0;   // Hidden, but necessary to validate the form
    }
    else
      this.transaction.number = results.data.nextBulkContainerNumber;

    await this.refresh();

    this.init = false;
    if (!this.transactionId) {
      this.$nextTick(() => {this.$refs.form.validate()});
      this.edit = true;
    }
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    allContents() {
      this.transaction.volume = this.vessel.contents.volume;
      this.transaction.litres = this.vessel.contents.litres;
      this.transaction.kg = this.vessel.contents.kg;
      this.transaction.strength = this.vessel.contents.strength;
    },
    async refresh() {
      let results = await this.$apiCall(`query ExciseVesselTransferToBulkContainer($id:ID!,$asof:DateTime!) {
                                            exciseVessel(id:$id) {
                                              id,name,tare,
                                              contents(date:$asof){volume,litres,alcoholName,alcoholType,alcoholId,siteId,strength}
                                            }
                                          }`,
                                      {id:this.transaction ? this.transaction.vesselId : this.vesselId,asof:this.transaction.date});
      if (!results.error) {
        this.vessel = results.data.exciseVessel;
        this.bulkContainers = [];
        if (this.vessel.contents.siteId)   // Can happen if bulk container didn't exist yet
          await this.$lazyListLoad(this.bulkContainers, 'bulkContainers2', `id,name,contents {alcoholType,volume,alcoholName,alcoholId}`, 100, [{field:'siteId', comparator:'eq', value:this.vessel.contents.siteId}], [{name:'asof', value:this.transaction.date}]);
        if (!this.transactionId) {
          if (!this.bulkContainers.some(b => b.id === this.transaction.destinationBulkContainerId))
            this.transaction.destinationBulkContainerId = this.bulkContainers.length ? this.bulkContainers[0].id : null;
          this.create = this.transaction.destinationBulkContainerId == null;
          this.transaction.strength = this.vessel.contents.strength;
        }
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        this.saving = true;
        const input = {...this.transaction};
        if (this.create)
          delete input.destinationBulkContainerId;
        const results = await this.$apiCall(`mutation ExciseVesselTransferToBulkContainer($input: ExciseVesselTransferToBulkContainerInput!) {
                                                exciseVesselTransferToBulkContainerCreate(input:$input) {id}
                                              }`,
                                            {input:input});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} move contents to bulk container transaction for ${this.vessel.name}`});
        }
      }
		}
	}
}
</script>